import { assetsMap, type Asset, type NodeRecord } from '@flobi/artwork-core';
import { LOCAL_ORIGIN, type OpenDocument } from '../useEditorDocument';
import type { Binding, GenerationClient, Reference, Request } from '../ai/contracts';
import { generationFailureMessage } from '../ai/generationFailure';
import { needsMediaResolution, readableMediaUrl, corsReadUrl } from '../../services/mediaRead';
import { MASK_MODEL, UPSCALE_MODEL, toolPrompt, type Strength, type ToolDef } from './catalogue';
import { upscaleFactor, type UpscaleTarget } from './upscaleSizes';

/*
 * A tool run on a picture or clip: the request built from the layer's own saved picture (never
 * drawn and uploaded again — it already is one), scaled first only when the model refuses its size,
 * then recorded in the file's runs (`artworkAiRuns`) like any generation — so the canvas glows round
 * the layer, the zoom pill's bar counts it, Stop works, and every viewer of the file sees it.
 * Generate's run loop (ArtworkGeneration) follows it and lands the result (landTool).
 */

export interface ToolChoices { typed?: string; mask?: Blob | null; ratio?: string; count?: number; strength?: Strength; target?: UpscaleTarget }

type SavedAsset = Asset & { mediaAssetId?: string; src?: string; mime?: string; width?: number; height?: number; duration?: number; name?: string; kind?: string };
/** A layer that shows a picture: an image (or clip) layer, or a shape filled with one — every Generate result is the latter. */
export interface PictureLayer { id: string; name?: string; width: number; height: number; transform: number[]; clip?: { duration: number } | null; fit?: string; assetId: string; fill: number | null }

export function pictureLayer(open: Pick<OpenDocument, 'scene'>, layerId: string): PictureLayer | null {
  const node = open.scene.get(layerId) as (NodeRecord & { width?: number; height?: number; fills?: { type: string; visible?: boolean; assetId?: string; fit?: string }[]; assetId?: string; clip?: { duration: number } | null; fit?: string }) | undefined;
  if (!node || !(Number(node.width) > 0) || !(Number(node.height) > 0)) return null;
  const base = { id: layerId, name: node.name, width: node.width!, height: node.height!, transform: [...node.transform] };
  if (node.type === 'image' && node.assetId) return { ...base, clip: node.clip ?? null, fit: node.fit, assetId: node.assetId, fill: null };
  if (!['rect', 'ellipse', 'polygon', 'star', 'path', 'boolean'].includes(node.type)) return null;
  const fills = node.fills ?? [];
  const at = fills.findLastIndex(paint => paint.type === 'image' && paint.visible !== false && paint.assetId);
  return at < 0 ? null : { ...base, fit: fills[at]!.fit, assetId: fills[at]!.assetId!, fill: at };
}


/**
 * Whether the layer shows more than its picture: a visible effect (Adjust, a shadow, a blur, a
 * shader…), another visible fill over or under the picture, or a stroke. Every tool then works on
 * the layer as ONE picture, what the person sees (Tarek, 15 Oct: "if I adjusted hue or slapped an
 * effect on top … then did a layerize, it doesn't take that into consideration"; 2 Nov: "all
 * should treat it as one image"), and a result that replaces it lands flattened (landResult).
 */
export function looksBeyondItsPicture(open: Pick<OpenDocument, 'scene'>, layerId: string, cropCounts = true): boolean {
  const node = open.scene.get(layerId) as { crop?: object; effects?: { visible?: boolean }[]; fills?: { type: string; visible?: boolean; assetId?: string; crop?: object }[]; strokes?: { visible?: boolean }[] } | undefined;
  if (!node) return false;
  const shown = <T extends { visible?: boolean }>(list: readonly T[] | undefined) => (list ?? []).filter(item => item.visible !== false);
  // A cropped picture is the part that shows: that is what a tool makes layers of. A tool whose
  // result replaces the picture works on all of it, and the layer keeps its crop (`cropCounts`).
  if (cropCounts && (node.crop || shown(node.fills).some(paint => paint.crop))) return true;
  if (shown(node.effects).length) return true;
  if (shown(node.strokes).length) return true;
  return shown(node.fills).length > 1;
}

/**
 * Draws the layer as it looks from its pictures' full originals, at their own resolution and at
 * most `maxEdge` px on its long side (the canvas's `renderLayerPng`).
 */
export type LayerRender = (layerId: string, maxEdge: number) => Promise<Uint8Array | null>;

/** The most a layer is drawn at for a tool: past this a GPU surface may not be had. */
const RENDER_MAX = 8192;

/**
 * Whether a tool's result lands back in the same frame, the layer's own crop and fit kept: the
 * picture is then sent whole, at its native size (Tarek, 3 Nov: "always run on the original"). A
 * brushed area is marked on the frame as seen, so it is sent as seen.
 */
const keepsTheFrame = (tool: ToolDef, choices: ToolChoices) => tool.land === 'replace' && !choices.mask;

/** Requests that carry the layer drawn as it looks rather than its picture file (`toolRequest`). */
const drawnAsItLooks = new WeakSet<Request>();

export function sourceOf(open: Pick<OpenDocument, 'root' | 'scene'>, layerId: string): { node: PictureLayer; asset: SavedAsset } | null {
  const node = pictureLayer(open, layerId);
  if (!node) return null;
  const asset = assetsMap(open.root).get(node.assetId) as SavedAsset | undefined;
  return asset?.src ? { node, asset } : null;
}

/** The picture at most `edge` px a side (and under `bytes`), as a file to upload. */
async function scaled(url: string, edge: number, bytes = Infinity): Promise<Blob> {
  const image = new Image(); image.crossOrigin = 'anonymous'; image.src = corsReadUrl(url);
  await image.decode();
  const k = Math.min(1, edge / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * k)); canvas.height = Math.max(1, Math.round(image.naturalHeight * k));
  canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
  const encode = (type: string, quality?: number) => new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(Error('The picture could not be prepared.')), type, quality));
  const png = await encode('image/png');
  return png.size <= bytes ? png : encode('image/webp', 0.9);
}

/** A picture's own pixel size, read from the file itself. */
async function naturalSize(asset: SavedAsset): Promise<{ width: number; height: number }> {
  const image = new Image(); image.crossOrigin = 'anonymous'; image.src = corsReadUrl(await readableMediaUrl(asset.src!, asset.mediaAssetId));
  await image.decode();
  return { width: image.naturalWidth, height: image.naturalHeight };
}

/** The colour a brushed area is marked in on the picture sent (strokes from MaskBrush, laid over at 55%). */
export const HIGHLIGHT = '#ff00ff';

/** The picture (at most 2048 px a side) with the brushed strokes laid over it, translucent. */
async function highlighted(url: string, strokes: Blob): Promise<Blob> {
  const image = new Image(); image.crossOrigin = 'anonymous'; image.src = corsReadUrl(url);
  await image.decode();
  const overlay = await createImageBitmap(strokes);
  const k = Math.min(1, 2048 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * k)); canvas.height = Math.max(1, Math.round(image.naturalHeight * k));
  const g = canvas.getContext('2d')!;
  g.drawImage(image, 0, 0, canvas.width, canvas.height);
  g.globalAlpha = 0.55; g.drawImage(overlay, 0, 0, canvas.width, canvas.height); g.globalAlpha = 1;
  overlay.close();
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(Error('The picture could not be marked.')), 'image/png'));
}

/** The request a tool sends for a layer: its picture (or clip), the mask, the words, the tool's settings. */
/** `quoteOnly`: nothing is scaled or uploaded — the price reads the model and settings, not the bytes. */
export async function toolRequest(open: OpenDocument, client: GenerationClient, layerId: string, tool: ToolDef, choices: ToolChoices, quoteOnly = false, render?: LayerRender): Promise<Request> {
  const source = sourceOf(open, layerId);
  if (!source) throw Error('Choose a picture or a clip first.');
  const { node, asset } = source;
  const destination = { documentId: open.documentId, pageId: open.pageId };
  const area = !!choices.mask;
  // Edit with Words on a brushed area paints inside the mask (FLUX Fill), the whole picture otherwise.
  const model = area ? MASK_MODEL : choices.target ? UPSCALE_MODEL : tool.model!;
  const inputs: Record<string, unknown> = { ...(tool.inputs ?? {}) };
  if (choices.ratio) inputs.aspect_ratio = choices.ratio;
  if (choices.count) inputs.num_layers = choices.count;
  if (tool.kind === 'video') {
    // The quote too: a clip model is priced by the clip's seconds, which ai-back reads from the
    // file — a Drive route it cannot open was quoted at its 15 s fallback (Upscale said 1,800 for 6 s).
    if (tool.clipSeconds) {
      const seconds = Number(asset.duration || node.clip?.duration) || 0;
      if (seconds && (seconds < tool.clipSeconds.min || seconds > tool.clipSeconds.max + 0.5)) {
        throw Error(`${tool.label} takes clips of ${tool.clipSeconds.min} to ${tool.clipSeconds.max} seconds. This one is ${Math.round(seconds)}.`);
      }
      // Whole seconds: the model cuts from the start, so a 6.04 s clip comes back 6 s, never padded.
      if (seconds) inputs.duration = Math.max(tool.clipSeconds.min, Math.min(tool.clipSeconds.max, Math.floor(seconds)));
    }
    inputs.videoUrl = needsMediaResolution(asset.src!, asset.mediaAssetId) ? await readableMediaUrl(asset.src!, asset.mediaAssetId) : asset.src;
    return { kind: 'video', model, prompt: toolPrompt(tool, choices.typed ?? '', false, choices.strength), quantity: 1, inputs, references: [], destination };
  }
  let picture: Reference;
  // The picture's own size; read from the file when the file's record lacks it — never the
  // layer's size on the page, which a tool would take for the picture's (the result came back small).
  const own = asset.width && asset.height ? { width: asset.width, height: asset.height } : await naturalSize(asset).catch(() => null);
  const width = own?.width || node.width, height = own?.height || node.height;
  const tooBig = tool.maxEdge && Math.max(width, height) > tool.maxEdge;
  let flat: Blob | null = null;
  const drawn = !quoteOnly && !!render && looksBeyondItsPicture(open, layerId, !keepsTheFrame(tool, choices));
  if (drawn) {
    // As it looks, drawn from the full original at its own resolution (never the copy on screen,
    // which is the size the layer is on the page), within what the model takes.
    const longEdge = tool.maxEdge ?? RENDER_MAX;
    const bytes = await render!(layerId, longEdge);
    if (!bytes) throw Error('The original picture could not be loaded to draw it with its effects. Try again in a moment.');
    let blob = new Blob([bytes as BlobPart], { type: 'image/png' });
    if (tool.maxBytes && blob.size > tool.maxBytes) {
      const url = URL.createObjectURL(blob);
      try { blob = await scaled(url, longEdge, tool.maxBytes); } finally { URL.revokeObjectURL(url); }
    }
    flat = blob;
    picture = await client.reference(blob, `${node.name || 'Picture'}.png`, 'canvas', layerId);
  } else if (!quoteOnly && (tooBig || (tool.maxBytes && (asset as { bytes?: number }).bytes && (asset as { bytes?: number }).bytes! > tool.maxBytes) || !asset.mediaAssetId)) {
    // Scaled to what the model takes (Bria's delayer: 2048 px a side, 10 MB), then uploaded.
    const blob = await scaled(await readableMediaUrl(asset.src!, asset.mediaAssetId), tool.maxEdge ?? RENDER_MAX, tool.maxBytes);
    picture = await client.reference(blob, `${node.name || 'Picture'}.png`, 'canvas', layerId);
  } else {
    // The original itself: by its media id, at its own size.
    picture = { id: `${asset.mediaAssetId}:${layerId}`, name: asset.name || node.name || 'Picture', url: asset.src!, mime: asset.mime || 'image/png', width, height, mediaAssetId: asset.mediaAssetId, objectId: layerId, origin: 'canvas' };
  }
  if (choices.target) {
    // From the picture as sent: drawn with its effects, it can be another size than the file.
    const factor = upscaleFactor(choices.target, picture.width || width, picture.height || height);
    if (!factor) throw Error(`This picture is already ${choices.target} or larger.`);
    inputs.upscale_factor = factor;
  }
  // A brushed area: the picture itself, the area highlighted (catalogue MASK_MODEL's note).
  if (choices.mask && !quoteOnly) {
    // On the picture as sent: drawn as it looks (the brush's frame), else the file itself.
    const flatUrl = flat ? URL.createObjectURL(flat) : null;
    let marked: Blob;
    try { marked = await highlighted(flatUrl ?? await readableMediaUrl(asset.src!, asset.mediaAssetId), choices.mask); } finally { if (flatUrl) URL.revokeObjectURL(flatUrl); }
    picture = await client.reference(marked, `${node.name || 'Picture'} marked.png`, 'canvas', layerId);
  }
  const request: Request = { kind: 'image', model, prompt: toolPrompt(tool, choices.typed ?? '', area, choices.strength), quantity: 1, inputs, references: [picture], destination };
  if (drawn) drawnAsItLooks.add(request);
  return request;
}

/** The picture's size as it was sent (drawn with its effects, or the file itself): the size layers made from it come back to. */
function sentSize(request: Request, asset?: SavedAsset): { width: number; height: number } | null {
  const sent = request.references[0];
  if (sent?.width && sent.height) return { width: sent.width, height: sent.height };
  return asset?.width && asset.height ? { width: asset.width, height: asset.height } : null;
}


/** Records the run on the layer and starts it; failures are written on the run (and said by the caller). */
export async function startToolRun(open: OpenDocument, client: GenerationClient, layerId: string, tool: ToolDef, request: Request, etaSeconds?: number, strength?: Strength): Promise<string> {
  const store = open.root.getMap<Binding>('artworkAiRuns');
  const source = sourceOf(open, layerId);
  const key = crypto.randomUUID();
  const binding: Binding = { key, documentId: open.documentId, pageId: open.pageId, targetId: layerId, targetName: source?.node.name || tool.label,
    createdTarget: false, request, createdAt: Date.now(), status: 'requesting', results: [], applied: false,
    tool: { id: tool.id, label: tool.label, land: tool.land!, ...(drawnAsItLooks.has(request) ? { flattened: true } : {}), ...(sentSize(request, source?.asset) ? { source: sentSize(request, source?.asset)! } : {}), ...(tool.id === 'focus' && strength ? { strength } : {}) },
    ...(etaSeconds ? { etaSeconds } : {}) };
  const patch = (next: Partial<Binding>) => { const value = store.get(key); if (value) open.root.transact(() => store.set(key, { ...value, ...next }), LOCAL_ORIGIN); };
  open.root.transact(() => store.set(key, binding), LOCAL_ORIGIN);
  let submitted = false;
  try {
    submitted = true;
    const run = await client.generate(request);
    patch({ runId: run.id, aiJobId: run.aiJobId, status: run.status === 'completed' ? 'saving' : run.status, results: run.results, message: run.userMessage });
  } catch (problem) {
    const message = generationFailureMessage(problem, submitted);
    patch({ status: 'failed', message, applied: true });
    throw Error(message);
  }
  return key;
}
